import { FormError } from '@/components/form-error';
import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
} from '@/components/ui/card';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { PasswordInput } from '@/components/ui/input-password';
import { useAuthContext } from '@/context/auth-provider';
import { toast } from '@/hooks/use-toast';
import { changePasswordMutationFn } from '@/lib/api';
import { useZodForm } from '@/lib/zod';
import { ChangePasswordSchema } from '@/schemas';
import { TChangePassword } from '@/types/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { LoaderCircleIcon, SaveIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import FormattedMessage from '../formatted-message';

export default function ChangePasswordForm() {
    const [error, setError] = useState('');
    const queryClient = useQueryClient();

    const { user } = useAuthContext();
    const lastPasswordChange = format(
        user.security.lastPasswordChange,
        'E, d MMM, yyyy, hh:mm aa',
    );
    const { mutate, isPending } = useMutation({
        mutationFn: changePasswordMutationFn,
    });

    const changePasswordForm = useZodForm(ChangePasswordSchema, {
        defaultValues: {
            oldPassword: '',
            password: '',
            confirmPassword: '',
        },
        mode: 'onChange',
    });

    function onChangePassword(values: TChangePassword) {
        setError('');
        mutate(values, {
            onSuccess: (response) => {
                changePasswordForm.reset();
                queryClient.invalidateQueries({
                    queryKey: ['authUser'],
                    refetchType: 'all',
                });
                toast({
                    title: 'Password',
                    description: (
                        <FormattedMessage message={response.data.message} />
                    ),
                });
            },
            onError: (error) => {
                setError(error.message);
            },
        });
    }

    useEffect(() => {
        const sub = changePasswordForm.watch((_, { name }) => {
            if (name === 'password' || name === 'confirmPassword') {
                changePasswordForm.trigger(['password', 'confirmPassword']);
            }
        });
        return () => sub.unsubscribe();
    }, [changePasswordForm]);

    return (
        <>
            <div className=' max-w-sm flex flex-col  py-6 px-2'>
                <span className='font-bold text-lg'>Password</span>
                <p className='text-sm text-muted-foreground'>
                    To protect your account, passwords must adhere to the
                    following best practices:
                </p>{' '}
                <ol className='text-sm text-muted-foreground list-disc pl-8'>
                    <li className='mt-8'>Minimum length of 8-12 characters.</li>{' '}
                    <li>
                        Include at least one uppercase letter, one lowercase
                        letter, one number, and one special character (e.g.,
                        !@#\$%).
                    </li>
                    <li>
                        Do not use common words, sequential characters
                        (&quot;1234&quot;), or previously used passwords.
                    </li>
                </ol>
            </div>
            <Card className='flex-1 border-none bg-accent/50'>
                <CardHeader>
                    <h3 className='text-center'>Change your password</h3>
                </CardHeader>
                <CardContent className='space-y-4 p-2 md:p-6'>
                    <CardDescription className='mb-4 text-center'>
                        Enter your current password, choose a new password,{' '}
                        <br />
                        and confirm the new password.
                    </CardDescription>
                    <div className='flex items-center gap-4'>
                        <Form {...changePasswordForm}>
                            <form
                                className='w-full space-y-4'
                                onSubmit={changePasswordForm.handleSubmit(
                                    onChangePassword,
                                )}>
                                <FormField
                                    control={changePasswordForm.control}
                                    name='oldPassword'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className='text-[0.8rem] md:text-sm'>
                                                Current Password
                                            </FormLabel>
                                            <FormControl>
                                                <PasswordInput
                                                    {...field}
                                                    placeholder='******'
                                                    disabled={isPending}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={changePasswordForm.control}
                                    name='password'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className='text-[0.8rem] md:text-sm'>
                                                New Password
                                            </FormLabel>
                                            <FormControl>
                                                <PasswordInput
                                                    {...field}
                                                    placeholder='******'
                                                    disabled={isPending}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={changePasswordForm.control}
                                    name='confirmPassword'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className='text-[0.8rem] md:text-sm'>
                                                Confirm New Password
                                            </FormLabel>
                                            <FormControl>
                                                <PasswordInput
                                                    {...field}
                                                    placeholder='******'
                                                    disabled={isPending}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormError
                                    message={error}
                                    iconStyle='mx-auto'
                                />

                                <CardFooter className='flex-col md:!flex-row !justify-between items-center w-full gap-y-4'>
                                    <Button
                                        type='submit'
                                        disabled={isPending}>
                                        {isPending ? (
                                            <>
                                                <LoaderCircleIcon className='size-5 animate-spin' />{' '}
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <SaveIcon className='size-5' />{' '}
                                                Save password
                                            </>
                                        )}
                                    </Button>

                                    <p className='text-[0.68rem] md:text-[0.7rem]  text-end'>
                                        Last Change:{' '}
                                        <span className='text-muted-foreground'>
                                            {lastPasswordChange}
                                        </span>
                                    </p>
                                </CardFooter>
                            </form>
                        </Form>
                    </div>
                </CardContent>
            </Card>
        </>
    );
}
